/**
 * Core scenes: fire triangle, extinguisher match, CPR rhythm - drawn as
 * technical safety diagrams on the shared `<Blueprint>` stage (scenes-shared.tsx).
 * Step-bound art sits in `<On>`; the two ambient scenes carry no steps.
 * See index.tsx for the dispatch.
 */

import { Blueprint, On, Verdict } from './scenes-shared';

/** Fire triangle blueprint: palivo · kyslík · teplo; the fuel side fades out and the flame dies - loops. */
export function FireTriangleScene({ en }: { en: boolean }) {
  return (
    <Blueprint
      id="fireTriangle"
      label={en ? 'The fire triangle' : 'Požární trojúhelník'}
      title={en ? 'Fire triangle' : 'Požární trojúhelník'}
      source="z. 133/1985 Sb. · vyhl. 246/2001 Sb."
    >
      {/* sides - fuel side cycles out with the flame (hbA-fuel-cycle) */}
      <line className="hbB-draw-heavy hbA-side-fuel" x1="116" y1="226" x2="240" y2="48" />
      <line className="hbB-draw-heavy" x1="240" y1="48" x2="364" y2="226" />
      <line className="hbB-draw-heavy" x1="364" y1="226" x2="116" y2="226" />
      <circle className="hbB-ink" cx="116" cy="226" r="4" />
      <circle className="hbB-ink" cx="240" cy="48" r="4" />
      <circle className="hbB-ink" cx="364" cy="226" r="4" />
      {/* vertex labels */}
      <text className="hbB-lbl hbA-label-fuel" x="164" y="140" textAnchor="end">
        {en ? 'FUEL' : 'PALIVO'}
      </text>
      <text className="hbB-lbl" x="316" y="140">
        {en ? 'OXYGEN' : 'KYSLÍK'}
      </text>
      <text className="hbB-lbl" x="240" y="250" textAnchor="middle">
        {en ? 'HEAT' : 'TEPLO'}
      </text>
      {/* flame - hbA-flame-cycle is anchored at 240,200 */}
      <g className="hbA-flame">
        <path
          className="hbB-flame"
          d="M240 200 c-26 -18 -34 -44 -18 -66 c6 14 14 20 14 20 c-4 -26 8 -44 22 -56 c-2 20 10 30 16 44 c10 24 -6 48 -34 58 z"
        />
        <path
          className="hbB-bolt hbA-flame-inner"
          d="M240 195 c-12 -10 -16 -24 -8 -36 c4 8 10 12 10 12 c-2 -14 6 -22 12 -28 c0 12 8 18 10 26 c4 14 -8 24 -24 26 z"
        />
      </g>
      <text className="hbB-cap" x="464" y="76" textAnchor="end">
        {en ? '− 1 side → out' : '− 1 strana → uhasne'}
      </text>
    </Blueprint>
  );
}

type Suit = 'ok' | 'no' | 'lim';

const SUIT_FILL = {
  ok: 'hbB-ok-fill',
  no: 'hbB-no-fill',
  lim: 'hbB-warn-fill',
} satisfies Record<Suit, string>;

/** Agent × fire-class suitability table (ČSN EN 2). Column x of the three class cells; each 84 wide, rows 36 tall. */
const CLASS_X = [88, 172, 256] as const;
const ROW_Y0 = 82;
const ROW_H = 36;

/**
 * Extinguisher match - agent × fire-class table stays; each step highlights
 * the cell it judges: 1 water→wood ✓, 2 water→live panel ✗ (voda + proud),
 * 3 power off, then CO₂/powder ✓.
 */
export function ExtinguisherMatchScene({ en }: { en: boolean }) {
  const rows: { name: string; suits: readonly [Suit, Suit, Suit] }[] = [
    { name: en ? 'Water' : 'Voda', suits: ['ok', 'no', 'no'] },
    { name: en ? 'Foam' : 'Pěna', suits: ['ok', 'ok', 'no'] },
    { name: en ? 'Powder' : 'Prášek', suits: ['ok', 'ok', 'ok'] },
    { name: 'CO₂', suits: ['lim', 'ok', 'ok'] },
  ];
  const heads = [en ? 'solids' : 'pevné', en ? 'liquids' : 'kapaliny', en ? 'electric' : 'elektro'];
  return (
    <Blueprint
      id="extinguisherMatch"
      label={en ? 'The right extinguisher for the right fire' : 'Správný přístroj na správný požár'}
      title={en ? 'Agent × fire class (ČSN EN 2)' : 'Hasivo × třída požáru (ČSN EN 2)'}
      source="ČSN EN 2 · vyhl. 246/2001 Sb."
    >
      {/* header row */}
      <rect className="hbB-head" x="16" y="34" width="72" height="48" />
      <text className="hbB-soft" x="24" y="64">
        {en ? 'agent' : 'hasivo'}
      </text>
      {CLASS_X.map((x) => (
        <rect key={x} className="hbB-head" x={x} y="34" width="84" height="48" />
      ))}
      {/* class heads: letter left of the cell, pictogram right (never on top of each other) */}
      <text className="hbB-mono" x="96" y="56">
        A
      </text>
      {/* class A: flame over logs */}
      <g transform="translate(146 54) scale(0.85)">
        <path className="hbB-draw" d="M-10 8 h20 M-8 3 h16" />
        <path
          className="hbB-flame"
          d="M0 -2 c-6 -4 -7 -10 -2 -14 c1 4 3 5 3 5 c-1 -6 3 -9 5 -11 c0 5 3 7 4 10 c2 6 -3 11 -9 12 z"
        />
      </g>
      <text className="hbB-mono" x="180" y="56">
        B
      </text>
      {/* class B: flame over liquid */}
      <g transform="translate(230 54) scale(0.85)">
        <path className="hbB-draw" d="M-12 8 h24" />
        <path className="hbB-thin" d="M-12 3 q6 -4 12 0 t12 0" />
        <path
          className="hbB-flame"
          d="M0 -2 c-6 -4 -7 -10 -2 -14 c1 4 3 5 3 5 c-1 -6 3 -9 5 -11 c0 5 3 7 4 10 c2 6 -3 11 -9 12 z"
        />
      </g>
      {/* electrical: bolt, kept above the sub-label */}
      <path
        className="hbB-bolt"
        transform="translate(75 8) scale(0.75)"
        d="M300 40 l-9 16 h8 l-6 16 l16 -20 h-8 l7 -12 z"
      />
      {CLASS_X.map((x, i) => (
        <text key={x} className="hbB-soft" x={x + 42} y="76" textAnchor="middle">
          {heads[i]}
        </text>
      ))}
      {/* rows */}
      {rows.map((row, r) => {
        const y = ROW_Y0 + r * ROW_H;
        return (
          <g key={row.name}>
            <rect className="hbB-cell" x="16" y={y} width="72" height={ROW_H} />
            <text className="hbB-lbl" x="24" y={y + 24}>
              {row.name}
            </text>
            {CLASS_X.map((x, c) => {
              const suit = row.suits[c];
              const size = suit === 'lim' ? 12 : 14;
              return (
                <g key={x}>
                  <rect className="hbB-cell" x={x} y={y} width="84" height={ROW_H} />
                  <rect
                    className={SUIT_FILL[suit]}
                    x={x + 42 - size / 2}
                    y={y + ROW_H / 2 - size / 2}
                    width={size}
                    height={size}
                    rx="2"
                  />
                </g>
              );
            })}
          </g>
        );
      })}
      {/* legend */}
      <rect className="hbB-ok-fill" x="16" y="234" width="12" height="12" rx="2" />
      <text className="hbB-soft" x="34" y="245">
        {en ? 'suitable' : 'vhodné'}
      </text>
      <rect className="hbB-no-fill" x="128" y="234" width="12" height="12" rx="2" />
      <text className="hbB-soft" x="146" y="245">
        {en ? 'prohibited' : 'zakázané'}
      </text>
      <rect className="hbB-warn-fill" x="259" y="235" width="10" height="10" rx="2" />
      <text className="hbB-soft" x="276" y="245">
        {en ? 'limited' : 'omezeně'}
      </text>

      {/* step 1 - water on wood: class A cell */}
      <On step="1">
        <rect className="hbB-focus" x="86" y="80" width="88" height="40" rx="4" />
        <Verdict kind="ok" x={406} y={96} r={20} />
        <text className="hbB-ok-txt" x="406" y="138" textAnchor="middle">
          {en ? 'suitable' : 'vhodné'}
        </text>
        <text className="hbB-soft" x="406" y="162" textAnchor="middle">
          {en ? 'water → wood' : 'voda → dřevo'}
        </text>
      </On>

      {/* step 2 - water on a live panel: prohibition callout */}
      <On step="2">
        <rect className="hbB-focus" x="254" y="80" width="88" height="40" rx="4" />
        <circle className="hbB-card" cx="406" cy="82" r="30" />
        <path
          className="hbB-water"
          d="M394 84 c0 -8 8 -14 8 -20 c0 6 8 12 8 20 a8 8 0 0 1 -16 0 z"
        />
        <path className="hbB-bolt" d="M418 62 l-7 12 h6 l-5 12 l12 -15 h-6 l5 -9 z" />
        <circle className="hbB-pict-red" cx="406" cy="82" r="30" />
        <path className="hbB-pict-red" d="M385 61 L427 103" />
        <text className="hbB-no-txt" x="406" y="136" textAnchor="middle">
          {en ? 'water + power' : 'voda + proud'}
        </text>
        <text className="hbB-soft" x="406" y="158" textAnchor="middle">
          {en ? 'shock risk' : 'úraz proudem'}
        </text>
        <Verdict kind="no" x={406} y={196} r={20} />
      </On>

      {/* step 3 - power off, then CO₂ / powder on the panel */}
      <On step="3">
        <rect className="hbB-focus" x="254" y="152" width="88" height="76" rx="4" />
        <text className="hbB-num" x="406" y="66" textAnchor="middle">
          1
        </text>
        <text className="hbB-soft" x="406" y="88" textAnchor="middle">
          {en ? 'power off' : 'vypni proud'}
        </text>
        <text className="hbB-num" x="406" y="118" textAnchor="middle">
          2
        </text>
        <text className="hbB-soft" x="406" y="140" textAnchor="middle">
          {en ? 'CO₂ / powder' : 'CO₂ / prášek'}
        </text>
        <Verdict kind="ok" x={406} y={180} r={20} />
        <text className="hbB-ok-txt" x="406" y="218" textAnchor="middle">
          {en ? 'de-energised' : 'bez napětí'}
        </text>
      </On>
    </Blueprint>
  );
}

/**
 * CPR - numbered sequence (respond? → 155 → 30:2 → AED as soon as it arrives)
 * beside a dimensioned chest section: 100-120/min metronome, 5-6 cm depth. Ambient.
 */
export function CprRhythmScene({ en }: { en: boolean }) {
  const steps = en
    ? [
        ['Responds? Breathes?', 'shout, shake shoulders'],
        ['Call 155', 'send someone for AED'],
        ['30 : 2', 'compressions : breaths'],
        ['AED once it arrives', 'prompts · keep pressing'],
      ]
    : [
        ['Reaguje? Dýchá?', 'oslov, zatřes rameny'],
        ['Volej 155', 'pošli někoho pro AED'],
        ['30 : 2', 'stlačení : vdechy'],
        ['AED hned, jak dorazí', 'pokyny · stlačuj dál'],
      ];
  return (
    <Blueprint
      id="cprRhythm"
      label={en ? 'CPR rhythm and sequence' : 'Rytmus a postup resuscitace'}
      title={en ? 'CPR · sequence' : 'Resuscitace · postup'}
      source="ERC Guidelines 2025 · ZP §102"
    >
      {steps.map(([head, sub], i) => {
        const y = 52 + i * 44;
        return (
          <g key={head}>
            <text className="hbB-num" x="16" y={y}>
              {i + 1}
            </text>
            <text className="hbB-lbl" x="44" y={y}>
              {head}
            </text>
            <text className="hbB-soft" x="44" y={y + 20}>
              {sub}
            </text>
          </g>
        );
      })}
      {/* metronome */}
      <circle className="hbB-no-fill hbA-alert" cx="278" cy="64" r="10" />
      <text className="hbB-big" x="296" y="76">
        100-120
      </text>
      <text className="hbB-soft" x="296" y="100">
        {en ? 'compressions / min' : 'stlačení za minutu'}
      </text>
      {/* chest section with the pressing hand (hbA-press travels 14 units = the 5-6 cm dimension) */}
      <rect className="hbB-cell" x="312" y="150" width="140" height="56" rx="20" />
      <path className="hbB-ghost" d="M330 164 H434" />
      <g className="hbA-press">
        <rect className="hbB-ink" x="373" y="106" width="18" height="40" rx="6" />
        <ellipse className="hbB-ink" cx="382" cy="146" rx="22" ry="8" />
      </g>
      <line className="hbB-leader" x1="290" y1="150" x2="332" y2="150" />
      <line className="hbB-leader" x1="290" y1="164" x2="312" y2="164" />
      <line className="hbB-dim" x1="284" y1="150" x2="284" y2="164" />
      <line className="hbB-dim" x1="278" y1="150" x2="290" y2="150" />
      <line className="hbB-dim" x1="278" y1="164" x2="290" y2="164" />
      <text className="hbB-dim-txt" x="272" y="163" textAnchor="end">
        5-6 cm
      </text>
    </Blueprint>
  );
}
